import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Coerce an axios / fetch error response detail into a safe string for toasts.
 * FastAPI 422 validation errors return `detail` as an ARRAY of `{msg, loc, type}`
 * objects — passing that array straight into `toast.error()` (or rendering it in
 * JSX) crashes React with "Objects are not valid as a React child". This helper
 * flattens any shape into a single readable string and is the only thing we
 * should ever pass to toast.error for error responses.
 *
 * Usage:
 *   } catch (err) {
 *     toast.error(errMsg(err, 'FALLBACK MESSAGE'));
 *   }
 */
export function errMsg(errorOrDetail, fallback = 'Something went wrong') {
  // 1) Prefer explicit `.response.data.detail` (FastAPI shape)
  // 2) Then `.detail` (already-unwrapped responses)
  // 3) Then `.message` on plain JS Errors (network, abort, etc.)
  // 4) Finally, only treat the input itself as a detail if it's a string,
  //    array, or a small object with a recognised msg/message field. NEVER
  //    JSON-stringify a raw axios error (circular refs, huge, leaks tokens).
  const fromResponse = errorOrDetail?.response?.data?.detail;
  const fromDetail = errorOrDetail?.detail;
  const fromMessage = errorOrDetail?.message;

  const detail = fromResponse ?? fromDetail ??
    // If errorOrDetail itself is already a usable shape, accept it
    (typeof errorOrDetail === 'string' || Array.isArray(errorOrDetail)
      ? errorOrDetail
      : (errorOrDetail && typeof errorOrDetail === 'object' && (errorOrDetail.msg || errorOrDetail.message)
        ? errorOrDetail
        : undefined));

  const coerce = (d) => {
    if (d == null || d === '') return null;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      const parts = d
        .map(x => (x && typeof x.msg === 'string') ? x.msg
                : (typeof x === 'string') ? x : null)
        .filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    if (typeof d === 'object') {
      if (typeof d.msg === 'string') return d.msg;
      if (typeof d.message === 'string') return d.message;
      return null;
    }
    return String(d);
  };

  return coerce(detail) || coerce(fromMessage) || fallback;
}
